import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { toLocalizedNumber, toLocalizedPrice } from '../../utils/localeUtils';
import { FaMoneyBillWave, FaBoxOpen, FaChartBar, FaUsers, FaLightbulb } from 'react-icons/fa';
import './FarmerSalesInsights.css';

const FarmerSalesInsights = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week'); // week, month, year

  const [salesData] = useState({
    week: [
      { day: 'Mon', sales: 4500, orders: 12 },
      { day: 'Tue', sales: 5200, orders: 15 },
      { day: 'Wed', sales: 4800, orders: 13 },
      { day: 'Thu', sales: 6100, orders: 18 },
      { day: 'Fri', sales: 5500, orders: 16 },
      { day: 'Sat', sales: 7200, orders: 22 },
      { day: 'Sun', sales: 6800, orders: 20 }
    ],
    month: [
      { week: 'Week 1', sales: 28000, orders: 85 },
      { week: 'Week 2', sales: 32000, orders: 95 },
      { week: 'Week 3', sales: 35000, orders: 105 },
      { week: 'Week 4', sales: 38000, orders: 115 }
    ]
  });

  const [hourlyPattern] = useState([
    { hour: '6AM', orders: 2 },
    { hour: '8AM', orders: 8 },
    { hour: '10AM', orders: 15 },
    { hour: '12PM', orders: 22 },
    { hour: '2PM', orders: 18 },
    { hour: '4PM', orders: 25 },
    { hour: '6PM', orders: 20 },
    { hour: '8PM', orders: 12 }
  ]);

  const [topProducts] = useState([
    { name: 'Tomatoes', revenue: 12500, sales: 45, growth: 15 },
    { name: 'Spinach', revenue: 8900, sales: 32, growth: 8 },
    { name: 'Carrots', revenue: 7200, sales: 28, growth: 12 },
    { name: 'Potatoes', revenue: 5500, sales: 22, growth: -5 },
    { name: 'Cabbage', revenue: 4200, sales: 18, growth: 3 }
  ]);

  const [customerRetention = 78] = useState();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== 'farmer') {
          navigate('/');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error parsing user data:', error);
        setLoading(false);
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const getMaxValue = (data, key) => {
    return Math.max(...data.map(item => item[key]));
  };

  const getRevenueHeatmap = () => {
    // Mock heatmap data for last 30 days
    const days = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: Math.floor(Math.random() * 5000) + 2000
      });
    }
    return days;
  };

  const heatmapData = getRevenueHeatmap();
  const maxHeatmapValue = Math.max(...heatmapData.map(d => d.revenue));

  if (loading) {
    return (
      <div className="farmer-sales-insights-page">
        <Header />
        <div className="farmer-sales-insights-loading-state">
          <div className="farmer-sales-insights-spinner"></div>
          <p>{t('farmerSalesInsights.loading')}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="farmer-sales-insights-page">
      <Header />
      
      <div className="farmer-sales-insights-container">
        <div className="farmer-sales-insights-header">
          <div>
            <h1 className="farmer-sales-insights-title">{t('farmerSalesInsights.title')}</h1>
            <p className="farmer-sales-insights-subtitle">{t('farmerSalesInsights.subtitle')}</p>
          </div>
          <div className="farmer-sales-insights-time-range-selector">
            <button
              className={`farmer-sales-insights-time-btn ${timeRange === 'week' ? 'active' : ''}`}
              onClick={() => setTimeRange('week')}
            >
              {t('farmerSalesInsights.week')}
            </button>
            <button
              className={`farmer-sales-insights-time-btn ${timeRange === 'month' ? 'active' : ''}`}
              onClick={() => setTimeRange('month')}
            >
              {t('farmerSalesInsights.month')}
            </button>
            <button
              className={`farmer-sales-insights-time-btn ${timeRange === 'year' ? 'active' : ''}`}
              onClick={() => setTimeRange('year')}
            >
              {t('farmerSalesInsights.year')}
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="farmer-sales-insights-metrics-grid">
          <div className="farmer-sales-insights-metric-card">
            <div className="farmer-sales-insights-metric-icon" aria-hidden>
              <FaMoneyBillWave />
            </div>
            <div className="farmer-sales-insights-metric-content">
              <div className="farmer-sales-insights-metric-value">Rs. {toLocalizedPrice(salesData[timeRange].reduce((sum, d) => sum + d.sales, 0))}</div>
              <div className="farmer-sales-insights-metric-label">{t('farmerSalesInsights.totalRevenue')}</div>
              <div className="farmer-sales-insights-metric-change positive">↑ 12% from last {timeRange}</div>
            </div>
          </div>
          <div className="farmer-sales-insights-metric-card">
            <div className="farmer-sales-insights-metric-icon" aria-hidden>
              <FaBoxOpen />
            </div>
            <div className="farmer-sales-insights-metric-content">
              <div className="farmer-sales-insights-metric-value">{toLocalizedNumber(salesData[timeRange].reduce((sum, d) => sum + d.orders, 0))}</div>
              <div className="farmer-sales-insights-metric-label">{t('farmerSalesInsights.totalOrders')}</div>
              <div className="farmer-sales-insights-metric-change positive">↑ 8% from last {timeRange}</div>
            </div>
          </div>
          <div className="farmer-sales-insights-metric-card">
            <div className="farmer-sales-insights-metric-icon" aria-hidden>
              <FaChartBar />
            </div>
            <div className="farmer-sales-insights-metric-content">
              <div className="farmer-sales-insights-metric-value">Rs. {toLocalizedPrice(Math.round(salesData[timeRange].reduce((sum, d) => sum + d.sales, 0) / salesData[timeRange].reduce((sum, d) => sum + d.orders, 0)))}</div>
              <div className="farmer-sales-insights-metric-label">{t('farmerSalesInsights.averageOrderValue')}</div>
              <div className="farmer-sales-insights-metric-change positive">↑ 5% from last {timeRange}</div>
            </div>
          </div>
          <div className="farmer-sales-insights-metric-card">
            <div className="farmer-sales-insights-metric-icon" aria-hidden>
              <FaUsers />
            </div>
            <div className="farmer-sales-insights-metric-content">
              <div className="farmer-sales-insights-metric-value">{toLocalizedNumber(customerRetention)}%</div>
              <div className="farmer-sales-insights-metric-label">{t('farmerSalesInsights.customerRetention')}</div>
              <div className="farmer-sales-insights-metric-change positive">↑ 3% from last {timeRange}</div>
            </div>
          </div>
        </div>

        {/* Revenue Heatmap */}
        <div className="farmer-sales-insights-chart-section">
          <div className="farmer-sales-insights-chart-card">
            <h3 className="farmer-sales-insights-chart-title">{t('farmerSalesInsights.revenueHeatmap')}</h3>
            <div className="farmer-sales-insights-heatmap-container">
              {heatmapData.map((day, index) => {
                const intensity = (day.revenue / maxHeatmapValue) * 100;
                return (
                  <div key={index} className="farmer-sales-insights-heatmap-cell" style={{ opacity: Math.max(intensity / 100, 0.3) }}>
                    <div className="farmer-sales-insights-heatmap-tooltip">
                      <div className="farmer-sales-insights-tooltip-date">{day.date}</div>
                      <div className="farmer-sales-insights-tooltip-value">Rs. {toLocalizedPrice(day.revenue)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="farmer-sales-insights-heatmap-legend">
              <span>{t('farmerSalesInsights.less')}</span>
              <div className="farmer-sales-insights-legend-gradient"></div>
              <span>{t('farmerSalesInsights.more')}</span>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="farmer-sales-insights-charts-grid">
          {/* Sales Analytics */}
          <div className="farmer-sales-insights-chart-card">
            <h3 className="farmer-sales-insights-chart-title">{t('farmerSalesInsights.salesAnalytics')}</h3>
            <div className="farmer-sales-insights-sales-chart-container">
              <div className="farmer-sales-insights-chart-bars-container">
                {salesData[timeRange].map((data, index) => {
                  const maxSales = getMaxValue(salesData[timeRange], 'sales');
                  const height = (data.sales / maxSales) * 100;
                  return (
                    <div key={index} className="farmer-sales-insights-chart-bar-item">
                      <div className="farmer-sales-insights-bar-wrapper">
                        <div
                          className="farmer-sales-insights-chart-bar-fill"
                          style={{ height: `${height}%` }}
                          data-value={data.sales}
                        >
                          <span className="farmer-sales-insights-bar-value">Rs. {toLocalizedNumber((data.sales / 1000).toFixed(1))}k</span>
                        </div>
                      </div>
                      <div className="farmer-sales-insights-bar-label">{data.day || data.week}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Hourly Order Pattern */}
          <div className="farmer-sales-insights-chart-card">
            <h3 className="farmer-sales-insights-chart-title">{t('farmerSalesInsights.hourlyOrderPattern')}</h3>
            <div className="farmer-sales-insights-hourly-chart-container">
              <div className="farmer-sales-insights-farmer-sales-insights-hourly-bars">
                {hourlyPattern.map((data, index) => {
                  const maxOrders = Math.max(...hourlyPattern.map(d => d.orders));
                  const height = (data.orders / maxOrders) * 100;
                  return (
                    <div key={index} className="farmer-sales-insights-farmer-sales-insights-hourly-bar-item">
                      <div className="farmer-sales-insights-hourly-bar" style={{ height: `${height}%` }}>
                        <span className="farmer-sales-insights-hourly-value">{toLocalizedNumber(data.orders)}</span>
                      </div>
                      <div className="farmer-sales-insights-hourly-label">{data.hour}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="farmer-sales-insights-chart-insight">
              <span className="farmer-sales-insights-chart-insight-inner">
                <FaLightbulb aria-hidden className="farmer-sales-insights-insight-icon" /> {t('farmerSalesInsights.bestTimeToList')}
              </span>
            </div>
          </div>
        </div>

        {/* Top Products Performance */}
        <div className="farmer-sales-insights-chart-section">
          <div className="farmer-sales-insights-chart-card">
            <h3 className="farmer-sales-insights-chart-title">{t('farmerSalesInsights.topProductsPerformance')}</h3>
            <div className="farmer-sales-insights-products-performance-list">
              {topProducts.map((product, index) => (
                <div key={index} className="farmer-sales-insights-product-performance-item">
                  <div className="farmer-sales-insights-product-rank">#{index + 1}</div>
                  <div className="farmer-sales-insights-product-info">
                    <div className="farmer-sales-insights-product-name">{product.name}</div>
                    <div className="farmer-sales-insights-product-stats">
                      <span>Revenue: Rs. {toLocalizedPrice(product.revenue)}</span>
                      <span>Sales: {toLocalizedNumber(product.sales)}</span>
                    </div>
                  </div>
                  <div className={`farmer-sales-insights-product-growth ${product.growth > 0 ? 'positive' : 'negative'}`}>
                    {product.growth > 0 ? '↑' : '↓'} {toLocalizedNumber(Math.abs(product.growth))}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Customer Retention Graph */}
        <div className="farmer-sales-insights-chart-section">
          <div className="farmer-sales-insights-chart-card">
            <h3 className="farmer-sales-insights-chart-title">{t('farmerSalesInsights.customerRetentionTrend')}</h3>
            <div className="farmer-sales-insights-retention-chart">
              <div className="farmer-sales-insights-retention-line">
                {[65, 68, 72, 75, 78].map((value, index) => (
                  <div key={index} className="farmer-sales-insights-retention-point" style={{ left: `${index * 25}%`, bottom: `${value}%` }}>
                    <div className="farmer-sales-insights-point-value">{toLocalizedNumber(value)}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FarmerSalesInsights;

